import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { hasPanelAccess, isAdmin, isCoordinatorAllowedPath } from '@/lib/auth/access';
import ErrorBoundary from '@/components/ErrorBoundary';

// Lazy load pages for better performance
import { lazy, Suspense } from 'react';

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const UnauthorizedPage = lazy(() => import('@/pages/auth/UnauthorizedPage'));

// Main layout
const AppLayout = lazy(() => import('@/components/layout/AppLayout'));

// Bookings
const BookingsListPage = lazy(() => import('@/pages/bookings/BookingsListPage'));
const BookingDetailPage = lazy(() => import('@/pages/bookings/BookingDetailPage'));
const BookingCreatePage = lazy(() => import('@/pages/bookings/BookingCreatePage'));
const BookingEditPage = lazy(() => import('@/pages/bookings/BookingEditPage'));

// Institutions
const InstitutionsListPage = lazy(() => import('@/pages/institutions/InstitutionsListPage'));
const InstitutionDetailPage = lazy(() => import('@/pages/institutions/InstitutionDetailPage'));
const InstitutionCreatePage = lazy(() => import('@/pages/institutions/InstitutionCreatePage'));
const InstitutionEditPage = lazy(() => import('@/pages/institutions/InstitutionEditPage'));

// Tours
const ToursListPage = lazy(() => import('@/pages/tours/ToursListPage'));
const TourDetailPage = lazy(() => import('@/pages/tours/TourDetailPage'));
const TourCreatePage = lazy(() => import('@/pages/tours/TourCreatePage'));
const TourEditPage = lazy(() => import('@/pages/tours/TourEditPage'));

// Meals
const MealsPage = lazy(() => import('@/pages/meals/MealsPage'));
const MealItemDetailPage = lazy(() => import('@/pages/meals/MealItemDetailPage'));
const MealItemCreatePage = lazy(() => import('@/pages/meals/MealItemCreatePage'));
const MealItemEditPage = lazy(() => import('@/pages/meals/MealItemEditPage'));
const MealSetDetailPage = lazy(() => import('@/pages/meals/MealSetDetailPage'));
const MealSetCreatePage = lazy(() => import('@/pages/meals/MealSetCreatePage'));
const MealSetEditPage = lazy(() => import('@/pages/meals/MealSetEditPage'));

// Extras
const ExtrasPage = lazy(() => import('@/pages/extras/ExtrasPage'));
const ExtraCategoryDetailPage = lazy(() => import('@/pages/extras/ExtraCategoryDetailPage'));
const ExtraCategoryCreatePage = lazy(() => import('@/pages/extras/ExtraCategoryCreatePage'));
const ExtraCategoryEditPage = lazy(() => import('@/pages/extras/ExtraCategoryEditPage'));
const ExtraItemDetailPage = lazy(() => import('@/pages/extras/ExtraItemDetailPage'));
const ExtraItemCreatePage = lazy(() => import('@/pages/extras/ExtraItemCreatePage'));
const ExtraItemEditPage = lazy(() => import('@/pages/extras/ExtraItemEditPage'));

// Vehicles
const VehiclesPage = lazy(() => import('@/pages/vehicles/VehiclesPage'));
const VehicleDetailPage = lazy(() => import('@/pages/vehicles/VehicleDetailPage'));
const VehicleCreatePage = lazy(() => import('@/pages/vehicles/VehicleCreatePage'));
const VehicleEditPage = lazy(() => import('@/pages/vehicles/VehicleEditPage'));

// Calendar
const OperatingDaysPage = lazy(() => import('@/pages/calendar/OperatingDaysPage'));
const CalendarBlocksPage = lazy(() => import('@/pages/calendar/CalendarBlocksPage'));

// Reports
const DailyReportPage = lazy(() => import('@/pages/reports/DailyReportPage'));
const KitchenReportPage = lazy(() => import('@/pages/reports/KitchenReportPage'));
const FinancialReportPage = lazy(() => import('@/pages/reports/FinancialReportPage'));
const StaffWorkloadReportPage = lazy(() => import('@/pages/reports/StaffWorkloadReportPage'));
const DeclaredVsActualReportPage = lazy(() => import('@/pages/reports/DeclaredVsActualReportPage'));
const GastronomyReportPage = lazy(() => import('@/pages/reports/GastronomyReportPage'));
const AdvancePaymentReportPage = lazy(() => import('@/pages/reports/AdvancePaymentReportPage'));
const PackageSummaryReportPage = lazy(() => import('@/pages/reports/PackageSummaryReportPage'));

// Cash Settlements
const CashSettlementsListPage = lazy(() => import('@/pages/cash-settlements/CashSettlementsListPage'));
const CashSettlementFormPage = lazy(() => import('@/pages/cash-settlements/CashSettlementFormPage'));
const CashSettlementDetailPage = lazy(() => import('@/pages/cash-settlements/CashSettlementDetailPage'));

// Staff
const StaffListPage = lazy(() => import('@/pages/staff/StaffListPage'));

// Loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
  </div>
);

// Protected Route Component. Access rules live in src/lib/auth/access.ts (the
// single source of truth shared with the Sidebar):
//  - admins        → full access
//  - coordinators  → only the whitelisted sections; admin-only routes reached
//                    by direct URL are redirected back to /bookings
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Allow administrators and coordinators into the panel
  if (!hasPanelAccess(user)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Coordinators (non-admins) may only reach whitelisted sections
  if (!isAdmin(user) && !isCoordinatorAllowedPath(location.pathname)) {
    return <Navigate to="/bookings" replace />;
  }

  return <>{children}</>;
};

// Router configuration
export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/unauthorized',
    element: (
      <Suspense fallback={<PageLoader />}>
        <UnauthorizedPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },

  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <AppLayout />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      // Redirect root to bookings
      {
        index: true,
        element: <Navigate to="/bookings" replace />,
      },

      // Dashboard - UKRYTE: poza zakresem zamówienia
      {
        path: 'dashboard',
        element: <Navigate to="/bookings" replace />,
      },

      // Bookings routes
      {
        path: 'bookings',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <BookingsListPage />
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BookingCreatePage />
              </Suspense>
            ),
          },
          {
            path: ':bookingRef',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BookingDetailPage />
              </Suspense>
            ),
          },
          {
            path: ':bookingRef/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BookingEditPage />
              </Suspense>
            ),
          },
        ],
      },

      // Institutions routes
      {
        path: 'institutions',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <InstitutionsListPage />
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InstitutionCreatePage />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InstitutionDetailPage />
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InstitutionEditPage />
              </Suspense>
            ),
          },
        ],
      },

      // Tours routes
      {
        path: 'tours',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <ToursListPage />
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<PageLoader />}>
                <TourCreatePage />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <TourDetailPage />
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <TourEditPage />
              </Suspense>
            ),
          },
        ],
      },

      // Meals routes
      {
        path: 'meals',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <MealsPage />
              </Suspense>
            ),
          },
          // Meal Items routes
          {
            path: 'items/create',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MealItemCreatePage />
              </Suspense>
            ),
          },
          {
            path: 'items/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MealItemDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'items/:id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MealItemEditPage />
              </Suspense>
            ),
          },
          // Meal Sets routes
          {
            path: 'sets/create',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MealSetCreatePage />
              </Suspense>
            ),
          },
          {
            path: 'sets/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MealSetDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'sets/:id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MealSetEditPage />
              </Suspense>
            ),
          },
        ],
      },

      // Extras routes
      {
        path: 'extras',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <ExtrasPage />
              </Suspense>
            ),
          },
          // Extra Categories routes
          {
            path: 'categories/create',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ExtraCategoryCreatePage />
              </Suspense>
            ),
          },
          {
            path: 'categories/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ExtraCategoryDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'categories/:id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ExtraCategoryEditPage />
              </Suspense>
            ),
          },
          // Extra Items routes
          {
            path: 'items/create',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ExtraItemCreatePage />
              </Suspense>
            ),
          },
          {
            path: 'items/:id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ExtraItemDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'items/:id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ExtraItemEditPage />
              </Suspense>
            ),
          },
        ],
      },

      // Vehicles routes
      {
        path: 'vehicles',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <VehiclesPage />
              </Suspense>
            ),
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VehicleCreatePage />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VehicleDetailPage />
              </Suspense>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VehicleEditPage />
              </Suspense>
            ),
          },
        ],
      },

      // Calendar routes
      {
        path: 'calendar',
        children: [
          {
            path: 'operating-days',
            element: (
              <Suspense fallback={<PageLoader />}>
                <OperatingDaysPage />
              </Suspense>
            ),
          },
          {
            path: 'blocks',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CalendarBlocksPage />
              </Suspense>
            ),
          },
        ],
      },

      // Cash Settlements routes
      {
        path: 'cash-settlements',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <CashSettlementsListPage />
              </Suspense>
            ),
          },
          {
            path: 'new',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CashSettlementFormPage />
              </Suspense>
            ),
          },
          {
            path: ':date',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CashSettlementDetailPage />
              </Suspense>
            ),
          },
          {
            path: ':date/edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CashSettlementFormPage />
              </Suspense>
            ),
          },
        ],
      },

      // Reports routes
      {
        path: 'reports',
        children: [
          {
            path: 'daily',
            element: (
              <Suspense fallback={<PageLoader />}>
                <DailyReportPage />
              </Suspense>
            ),
          },
          {
            path: 'kitchen',
            element: (
              <Suspense fallback={<PageLoader />}>
                <KitchenReportPage />
              </Suspense>
            ),
          },
          {
            path: 'financial',
            element: (
              <Suspense fallback={<PageLoader />}>
                <FinancialReportPage />
              </Suspense>
            ),
          },
          {
            path: 'staff-workload',
            element: (
              <Suspense fallback={<PageLoader />}>
                <StaffWorkloadReportPage />
              </Suspense>
            ),
          },
          {
            path: 'declared-vs-actual',
            element: (
              <Suspense fallback={<PageLoader />}>
                <DeclaredVsActualReportPage />
              </Suspense>
            ),
          },
          {
            path: 'gastronomy',
            element: (
              <Suspense fallback={<PageLoader />}>
                <GastronomyReportPage />
              </Suspense>
            ),
          },
          {
            path: 'advance-payments',
            element: (
              <Suspense fallback={<PageLoader />}>
                <AdvancePaymentReportPage />
              </Suspense>
            ),
          },
          {
            path: 'package-summary',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PackageSummaryReportPage />
              </Suspense>
            ),
          },
        ],
      },

      // Staff routes
      {
        path: 'staff',
        element: (
          <Suspense fallback={<PageLoader />}>
            <StaffListPage />
          </Suspense>
        ),
      },
    ],
  },

  // Catch all - redirect to bookings
  {
    path: '*',
    element: <Navigate to="/bookings" replace />,
  },
]);
